import _, { debounce } from 'lodash'
import React, { FC, useEffect, useRef, useState } from 'react'

import { httpRequest } from '../../common/network'
import Fancybox from '../../fancybox.js'
import { useAppSelector } from '../../redux/hooks'
import BreadCrumbs from '../BreadCrumbs/BreadCrumbs'
import Chat from '../Chat'
import { MainContainer, Separator } from '../customComponents'
import {
  ArticleInfo,
  ArticleProps,
  FixedSidebarItem,
  Path,
  RelatedArticle,
  SidebarType,
  StatisticsArticle,
  TreeItem,
} from '../definitions'
import ElementNotFound from '../ElementNotFound'
import Feedback from '../Feedback/Feedback'
import HelpHeaderDetailed from '../Header/HelpHeaderDetailed'
import PrivacyMessage from '../PrivacyMessage'
import {
  ArticleContainer,
  ArticleContent,
  ArticleLoading,
  FroalaStatisticsContainer,
  sidebarOffsetTop,
} from './article.components'
import ArticleAdminMode from './ArticleAdminMode'
import ArticleReadOnlyMode from './ArticleReadOnlyMode'
import ArticleSidebar from './ArticleSidebar'
import ReadonlyArticleActions from './ReadonlyArticleActions'
import RelatedArticles from './RelatedArticles'

const maxBottomOffset = 500
const breadCrumbsHeight = 64

const Article: FC<ArticleProps> = (props: ArticleProps) => {
  const [sectionsRefs, setRefs] = useState<
    { node: HTMLDivElement; top: number }[]
  >([])
  const [article, setArticle] = useState<ArticleInfo>()
  const [statistics, setStatistics] = useState<StatisticsArticle>()
  const [currentSection, setCurrentSection] = useState<number>(0)
  const [elementNotFound, setElementNotFound] = useState<boolean>(false)
  const [fixedSidebar, setFixedSidebar] = useState<FixedSidebarItem>({
    fixedSideBar: false,
    fixedSideBarMaxBottom: false,
  })
  const [sideBar, setSidebar] = useState<SidebarType>([])
  const [tree, setTree] = useState<TreeItem>()
  const [viewportWidth, setViewportWidth] = useState<number>(window.innerWidth ?? 0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const container = useRef<HTMLDivElement>()
  const articleContainer = useRef<HTMLDivElement>()
  const helpHeaderDetailed = useRef<HTMLDivElement>()
  const editorModeActive = useAppSelector((state) => state.editor.value)
  const realm = useAppSelector((state) => state.realm.value)
  const visualizationMode = useAppSelector(
    (state) => state.visualizationMode.value
  )
  const adminMode = useAppSelector((state) => state.adminMode.value)

  useEffect( () => {
    //scrolling position moved here to avoid bug on toggle
    let current = 0
    if (_.size(sectionsRefs) === 0) {
      return
    }
    _.forEach(sectionsRefs, (section, sectionNumber) => {
      if (section.node?.offsetTop !== undefined) {
        section.top = section.node.offsetTop
      }

      if (section.top < container.current.scrollTop + 200) {
        current = sectionNumber
      }
    })

    setCurrentSection(current)

  })

  useEffect(() => {
    loadData(undefined)
    setFixedSidebar({ ...fixedSidebar, fixedSideBar: false })
  }, [props.searchKey, editorModeActive])

  useEffect(() => {
    updateViewportWidth()
    if (container.current && props.isExternal) {
      container.current.scrollTop = 0
    }
    window.addEventListener('resize', debounce(updateViewportWidth, 100))
    return () => {
      window.removeEventListener('resize', debounce(updateViewportWidth, 100))
    }
  }, [container.current, props.searchKey, editorModeActive])

  useEffect(() => {
    if (container.current && sideBar) {
      container.current.removeEventListener('scroll', listenScrollEvent)
      if (!editorModeActive) container.current.addEventListener('scroll', listenScrollEvent)
    }
  }, [sideBar, container, article, editorModeActive])

  //images gallery and zoom for article images, only for non admin
  useEffect(() => {
    if(!editorModeActive) {
      const elements: Element[] = Array.from(document.getElementById("articleContainer").getElementsByTagName('img'))
      elements.forEach((el: Element) => {
        el.setAttribute('data-fancybox','images')
        el.classList.add('cursor-pointer')
      })
    }
  })

  const loadData = (searchKey: string | undefined) => {
    if (props.addMode) return
    if (searchKey === undefined) {
      searchKey = props.searchKey
    }
    setIsLoading(true)
    httpRequest({
      method: 'GET',
      path: 'help/article/' + searchKey,
      params: {
        realm: realm,
        visualize_mode: adminMode ? visualizationMode : 0,
        edit_mode: editorModeActive ? 1 : 0,
      },
      onSuccess: (response: {
        data: ArticleInfo
        related_articles: RelatedArticle[]
        sidebar: SidebarType | boolean
        tree: TreeItem
        statistics: StatisticsArticle
      }) => {
        setArticle({
          ...response.data,
          related_articles: response.related_articles,
        })
        setTree(response.tree)
        setSidebar(typeof response.sidebar === 'boolean' ? [] : response.sidebar)
        setStatistics(response.statistics)
        setIsLoading(false)
      },
      onError: () => {
        setElementNotFound(true)
        setIsLoading(false)
      },
    })
  }

  const updateViewportWidth = () => {
    setViewportWidth(window.innerWidth)
  }

  const listenScrollEvent = () => {
    if (viewportWidth <= 768) {
      if (fixedSidebar.fixedSideBar) {
        setFixedSidebar({ ...fixedSidebar, fixedSideBar: false })
      }
      return
    }
    if (!articleContainer.current) return
    if (container.current) {
      const containerNode = container.current.parentElement
      const helpHeaderHeight = helpHeaderDetailed.current
        ? helpHeaderDetailed.current.offsetHeight
        : 0
      if (containerNode) {
        const offset =
          container.current.offsetTop +
          breadCrumbsHeight +
          helpHeaderHeight -
          (props.isExternal ? helpHeaderHeight : sidebarOffsetTop)
        const offsetBottom = articleContainer.current.getBoundingClientRect().bottom
        const hasSidebar = sideBar && sideBar.length > 0
        setFixedSidebar({
          fixedSideBar: hasSidebar ? container.current.scrollTop - 88 > offset && !fixedSidebar.fixedSideBar : false,
          fixedSideBarMaxBottom: hasSidebar ? offsetBottom <= maxBottomOffset && !fixedSidebar.fixedSideBarMaxBottom : false
        })
      }
    }
  }

  const getBreadCrumbs = () => {
    const breadCrumbPaths: Path[] = []
    let c = 3
    while (c >= 1) {
      if (tree && tree['section' + String(c) + '_permalink']) {
        const permalink = tree['section' + String(c) + '_permalink']
        breadCrumbPaths.push({
          id: tree['section' + String(c) + '_id'],
          text: tree['section' + String(c) + '_title'],
          onClick: () => props.changeView(permalink, 'sezione'),
        })
      }
      c--
    }

    return breadCrumbPaths
  }

  const renderSubArticle = () => {
    return (
      <>
        <HelpHeaderDetailed
          closeIcon={!props.embedded}
          onClickClose={props.onClickClose}
          onClick={() => props.changeView(article.parent_section, 'sezione')}
          title={article ? article.title : ''}
          isExternal={props.isExternal}
          ref={helpHeaderDetailed}
          embedded={props.embedded}
        />
        <BreadCrumbs
          onSelect={props.changeView}
          paths={getBreadCrumbs()}
          className={'breadCrumbs'}
          embedded={props.embedded}
          isExternal={props.isExternal}
        />
      </>
    )
  }

  const articleAdminMode = (
    <>
      <ArticleAdminMode
        article={article}
        hasSidebar={sideBar && sideBar.length > 0}
        statistics={statistics}
        setArticle={setArticle}
        changeView={props.changeView}
        subArticle={props.subArticle}
        isExternal={props.isExternal}
        viewportWidth={viewportWidth}
        updateSidebar={(newSidebar) => setSidebar(newSidebar)}
        fixedSidebar={fixedSidebar}
        embedded={props.embedded}
        articlePreview={() => {
          return <>{articleReadMode}</>
        }}
      />
    </>
  )

  const renderRelatedArticles = () => {
    return (
      <RelatedArticles
        relatedArticles={article.related_articles}
        changeView={props.changeView}
      />
    )
  }

  const articleReadMode = (
    <>
      <ArticleReadOnlyMode
        article={article}
        changeView={props.changeView}
        parentsArticle={props.parentsArticle}
        isInternal={props.isInternal}
        searchKey={props.searchKey}
        setReferences={setRefs}
        isExternal={props.isExternal}
        subArticle={props.subArticle}
        viewportWidth={viewportWidth}
      />
      {!adminMode && article && !props.subArticle && !article.feedback && (
        <Feedback article={Number(article.id)} />
      )}
      {article &&
        article.related_articles.length > 0 &&
        !props.subArticle &&
        renderRelatedArticles()}
    </>
  )

  const pageContent = (
    <ArticleContainer
      className={`articleContainer ${props.embedded ? 'embeddedArticle' : ''}`}
      subArticle={props.subArticle}
      ref={articleContainer}
      adminMode={adminMode}
      editorModeActive={editorModeActive}
    >
      {sideBar && sideBar.length > 0 && (
        <ArticleSidebar
          fixedSidebar={fixedSidebar}
          isExternal={props.isExternal}
          sideBar={sideBar}
          isArticle={!props.subArticle}
          currentSection={!editorModeActive ? currentSection : 0}
          editorModeActive={editorModeActive}
        />
      )}
      <>
        {adminMode && !isLoading && (
          <FroalaStatisticsContainer>
            {articleAdminMode}
          </FroalaStatisticsContainer>
        )}
        {!adminMode && article && (
            <Fancybox>
              <ArticleContent
                hasSidebar={sideBar && sideBar.length > 0}
                fixedSideBar={fixedSidebar.fixedSideBar}
                embedded={props.embedded}
                editorActive={false}
              >
                <ReadonlyArticleActions
                  editedArticlePermalink={article.permalink}
                  isExternal={props.isExternal}
                  realm={article.realm}
                  subArticle={props.subArticle}
                  viewportWidth={viewportWidth}
                  articleId={article.id}
                />
                {articleReadMode}
              </ArticleContent>
            </Fancybox>
        )}
      </>
    </ArticleContainer>
  )

  return (
    <MainContainer
      className={props.className}
      id={'articleContainer'}
      isExternal={props.isExternal}
      ref={container}
    >
      {!props.addMode && !props.subArticle && renderSubArticle()}
      {(!props.addMode && (!article || isLoading)) ? (
        elementNotFound ? (
          <ElementNotFound type={'article'} onClick={props.changeView} />
        ) : (
          <ArticleLoading
            className={'articleContainer'}
            isExternal={props.isExternal}
          />
        )
      ) : (
        <>
          {pageContent}
          {!editorModeActive &&
            !props.subArticle &&
            !elementNotFound &&
            article && (
              <>
                <Separator />
                <Chat type={'article'} id={Number(article.id)} />
                <PrivacyMessage />
              </>
            )}
        </>
      )}
    </MainContainer>
  )
}

export default Article
